import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Plus, Trash2, ArrowLeft, Save, Image as ImageIcon, Send, Mail, MessageSquare,
  Copy, Receipt, Download, Building2, ChevronDown, ChevronUp, X, Eye, UserPlus
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";

const emptyLI = { description: "", quantity: 1, unit_price: 0, unit: "ea" };
const stockPhotos = [
  "https://images.unsplash.com/photo-1781637202423-33ec5b47e52e?w=400&q=70",
  "https://images.unsplash.com/photo-1635424825057-7fb6dcd651ef?w=400&q=70",
  "https://images.unsplash.com/photo-1734303023491-db8037a21f09?w=400&q=70",
];
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const isoToDate = (s) => (s ? s.slice(0, 10) : "");
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const MAX_ITEMS = 50;

const Section = ({ title, right, children, tid }) => (
  <section className="bg-white border border-slate-200 rounded-md" data-testid={tid}>
    <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</div>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

export default function QuoteEditor() {
  const { qid } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [biz, setBiz] = useState(null);
  const [bizExpanded, setBizExpanded] = useState(false);
  const [quoteId, setQuoteId] = useState(qid || null);
  const [number, setNumber] = useState("");
  const [status, setStatus] = useState("draft");
  const [form, setForm] = useState({
    customer_id: params.get("customer") || "",
    title: "",
    line_items: [{ ...emptyLI }],
    notes: "",
    photos: [],
    tax_rate: 0,
    discount: 0,
    quote_date: new Date().toISOString().slice(0, 10),
    expiration_date: addDays(30),
    terms: "Net 30",
    signature: "",
    signer_name: "",
  });
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ channel: "email", to: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({ name: "", email: "", phone: "", address: "" });
  const [savingCust, setSavingCust] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, b] = await Promise.all([api.get("/customers"), api.get("/business")]);
      setCustomers(c.data); setBiz(b.data);
      if (qid) {
        const q = (await api.get(`/quotes/${qid}`)).data;
        setQuoteId(q.id); setNumber(q.number); setStatus(q.status);
        setForm({
          customer_id: q.customer_id, title: q.title,
          line_items: q.line_items.length ? q.line_items : [{ ...emptyLI }],
          notes: q.notes || "", photos: q.photos || [],
          tax_rate: q.tax_rate || 0, discount: q.discount || 0,
          quote_date: isoToDate(q.quote_date) || new Date().toISOString().slice(0, 10),
          expiration_date: isoToDate(q.expiration_date) || addDays(30),
          terms: q.terms || "Net 30",
          signature: q.signature || "", signer_name: q.signer_name || "",
        });
      } else {
        // Pull a one-shot prefill from the Calculator "Create Quote From Results" button
        try {
          const raw = sessionStorage.getItem("qq_prefill");
          if (raw) {
            const p = JSON.parse(raw);
            sessionStorage.removeItem("qq_prefill");
            setForm((f) => ({
              ...f,
              title: p.title || f.title,
              line_items: Array.isArray(p.line_items) && p.line_items.length ? p.line_items : f.line_items,
              notes: p.notes || f.notes,
            }));
            toast.success("Calculator values loaded — pick a customer and set your rates.");
          }
        } catch { /* ignore malformed prefill */ }
      }
    })();
  }, [qid]);

  const customer = useMemo(() => customers.find((c) => c.id === form.customer_id), [customers, form.customer_id]);

  const { subtotal, tax, total, balance } = useMemo(() => {
    const sub = form.line_items.reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
    const disc = Math.min(Number(form.discount || 0), sub);
    const t = (sub - disc) * (Number(form.tax_rate || 0) / 100);
    return { subtotal: sub, tax: t, total: sub - disc + t, balance: sub - disc + t };
  }, [form.line_items, form.discount, form.tax_rate]);

  const updateLI = (i, key, val) => {
    const arr = [...form.line_items];
    arr[i] = { ...arr[i], [key]: val };
    setForm({ ...form, line_items: arr });
  };
  const addLine = () => {
    if (form.line_items.length >= MAX_ITEMS) return toast.warning(`Max ${MAX_ITEMS} line items`);
    setForm({ ...form, line_items: [...form.line_items, { ...emptyLI }] });
  };
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, j) => j !== i) });

  const persist = async ({ silent } = {}) => {
    if (!form.customer_id) { toast.error("Pick a customer"); return null; }
    if (!form.title.trim()) { toast.error("Add a project title"); return null; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quote_date: form.quote_date ? new Date(form.quote_date).toISOString() : null,
        expiration_date: form.expiration_date ? new Date(form.expiration_date).toISOString() : null,
      };
      const res = quoteId ? await api.put(`/quotes/${quoteId}`, payload) : await api.post("/quotes", payload);
      setQuoteId(res.data.id); setNumber(res.data.number); setStatus(res.data.status);
      if (!silent) toast.success("Quote saved");
      return res.data;
    } catch (e) {
      if (e?.response?.status === 402) {
        setLimitOpen(true);
      } else {
        toast.error(formatError(e));
      }
      return null;
    }
    finally { setSaving(false); }
  };

  const goUpgrade = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); }
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    }));
    const urls = await Promise.all(readers);
    setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
    e.target.value = "";
  };

  const preview = async () => {
    const saved = await persist({ silent: true });
    if (saved) window.open(`${API}/quotes/${saved.id}/pdf`, "_blank");
  };
  const duplicate = async () => {
    const saved = await persist({ silent: true });
    if (!saved) return;
    try {
      const { data } = await api.post(`/quotes/${saved.id}/duplicate`);
      toast.success(`Duplicated as ${data.number}`);
      nav(`/dashboard/quotes/${data.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };
  const convert = async () => {
    const saved = await persist({ silent: true });
    if (!saved) return;
    try {
      const { data } = await api.post(`/invoices/from-quote/${saved.id}`);
      toast.success(`Invoice ${data.number} created`);
      nav(`/dashboard/invoices/${data.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };
  const openSend = async (channel) => {
    const saved = await persist({ silent: true });
    if (!saved || !customer) return;
    const link = `${window.location.origin}/quote/${saved.id}`;
    setSendForm({
      channel,
      to: channel === "email" ? (customer.email || "") : (customer.mobile || customer.phone || ""),
      message: `Hi ${customer.name},\n\nHere's your quote ${saved.number} — ${form.title}. Total: ${fmt(total)}.\n\nView & accept: ${link}`,
    });
    setSendOpen(true);
  };
  const doSend = async () => {
    try {
      await api.post("/communications/send", {
        ...sendForm,
        subject: `Your quote ${number}`,
        customer_id: form.customer_id, entity_type: "quote", entity_id: quoteId,
      });
      await api.patch(`/quotes/${quoteId}/status`, { status: "sent" });
      setStatus("sent");
      toast.success(`Quote sent via ${sendForm.channel}`);
      setSendOpen(false);
    } catch (e) { toast.error(formatError(e)); }
  };

  const statusColor = { draft: "status-draft", sent: "status-sent", viewed: "status-viewed", accepted: "status-accepted", rejected: "status-rejected" }[status];

  return (
    <div className="max-w-6xl mx-auto" data-testid="quote-editor">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => nav("/dashboard/quotes")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Quotes</button>
        {quoteId && <span className={`status-pill ${statusColor}`} data-testid="quote-status-pill">{status}</span>}
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            {quoteId ? `Quote ${number}` : "New quote"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Build, preview, and send in under 60 seconds.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="quote-actions">
          <Button data-testid="btn-save" variant="outline" onClick={() => persist({})} disabled={saving}><Save className="w-4 h-4 mr-1.5" /> Save</Button>
          <Button data-testid="btn-preview-pdf" variant="outline" onClick={preview} disabled={saving}><Eye className="w-4 h-4 mr-1.5" /> Preview PDF</Button>
          <Button data-testid="btn-send-email" variant="outline" onClick={() => openSend("email")} disabled={saving}><Mail className="w-4 h-4 mr-1.5" /> Email</Button>
          <Button data-testid="btn-send-sms" variant="outline" onClick={() => openSend("sms")} disabled={saving}><MessageSquare className="w-4 h-4 mr-1.5" /> SMS</Button>
          {quoteId && <Button data-testid="btn-duplicate" variant="outline" onClick={duplicate} disabled={saving}><Copy className="w-4 h-4 mr-1.5" /> Duplicate</Button>}
          {quoteId && <Button data-testid="btn-convert" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={convert} disabled={saving}><Receipt className="w-4 h-4 mr-1.5" /> Convert to Invoice</Button>}
        </div>
      </div>

      {/* SECTION 1 & 2: BUSINESS + BILL TO */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section
          tid="section-business"
          title="Business Information"
          right={
            <button className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => setBizExpanded(!bizExpanded)} data-testid="toggle-biz">
              {bizExpanded ? <><ChevronUp className="w-3 h-3" /> Hide</> : <><ChevronDown className="w-3 h-3" /> More</>}
            </button>
          }
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-blue-600" /></div>
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold text-slate-900">{biz?.company_name || "Set company name"}</div>
              <div className="text-slate-600">{biz?.company_email} {biz?.company_phone && `· ${biz.company_phone}`}</div>
              {biz?.company_address && <div className="text-slate-600">{biz.company_address}</div>}
              {bizExpanded && (
                <div className="mt-2 text-slate-600 text-xs space-y-0.5">
                  {biz?.website && <div>Web: {biz.website}</div>}
                  {biz?.owner_name && <div>Owner: {biz.owner_name}</div>}
                  <button onClick={() => nav("/dashboard/settings")} className="text-blue-600 hover:underline" data-testid="edit-biz">Edit in Settings →</button>
                </div>
              )}
            </div>
          </div>
        </Section>

        <Section tid="section-billto" title="Bill To">
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Customer</Label>
              <div className="flex gap-2 items-stretch">
                <div className="flex-1 min-w-0">
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="quote-customer"><SelectValue placeholder="Select a customer…" /></SelectTrigger>
                    <SelectContent>
                      {customers.length === 0 && <div className="text-xs text-slate-500 p-2">No customers yet. Click "New" to add one.</div>}
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="quote-new-customer-btn"
                  onClick={() => { setNewCust({ name: "", email: "", phone: "", address: "" }); setNewCustOpen(true); }}
                  className="shrink-0 whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4 mr-1.5" /> New
                </Button>
              </div>
            </div>
            {customer && (
              <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-slate-100 pt-3" data-testid="billto-preview">
                <div><span className="text-slate-400">Email:</span> {customer.email || "—"}</div>
                <div><span className="text-slate-400">Phone:</span> {customer.phone || "—"}</div>
                <div><span className="text-slate-400">Mobile:</span> {customer.mobile || "—"}</div>
                <div><span className="text-slate-400">Fax:</span> {customer.fax || "—"}</div>
                <div className="col-span-2"><span className="text-slate-400">Address:</span> {customer.address || "—"}</div>
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* SECTION 3: QUOTE INFO */}
      <div className="mt-4">
        <Section tid="section-quoteinfo" title="Quote Information">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Number</Label>
              <Input value={number || "auto"} disabled className="bg-slate-50 font-mono" data-testid="quote-number" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date</Label>
              <Input type="date" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} data-testid="quote-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Expires</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="quote-expires" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Terms</Label>
              <Select value={form.terms} onValueChange={(v) => setForm({ ...form, terms: v })}>
                <SelectTrigger data-testid="quote-terms"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Due on receipt", "Net 15", "Net 30", "Net 60", "50% deposit"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Status</Label>
              <Input value={status} disabled className="bg-slate-50 capitalize" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Title</Label>
            <Input data-testid="quote-title" placeholder="e.g. Driveway replacement — 24×20" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
        </Section>
      </div>

      {/* SECTION 4: LINE ITEMS */}
      <div className="mt-4">
        <Section
          tid="section-line-items"
          title={`Line Items (${form.line_items.length}/${MAX_ITEMS})`}
          right={<Button data-testid="add-line-item" size="sm" variant="outline" onClick={addLine}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 w-[50%]">Description</th>
                  <th className="text-right py-2">Rate</th>
                  <th className="text-right py-2">Qty</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((li, i) => (
                  <tr key={i} className="border-b border-slate-100 align-top" data-testid={`li-${i}`}>
                    <td className="py-2 pr-2">
                      <Textarea
                        rows={2}
                        placeholder="Description (supports multiple lines)"
                        value={li.description}
                        onChange={(e) => updateLI(i, "description", e.target.value)}
                        className="min-h-[38px] resize-y"
                        data-testid={`li-desc-${i}`}
                      />
                    </td>
                    <td className="py-2 pr-2 w-28">
                      <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLI(i, "unit_price", parseFloat(e.target.value) || 0)} className="text-right" data-testid={`li-rate-${i}`} />
                    </td>
                    <td className="py-2 pr-2 w-28">
                      <div className="flex gap-1">
                        <Input type="number" step="0.01" value={li.quantity} onChange={(e) => updateLI(i, "quantity", parseFloat(e.target.value) || 0)} className="text-right" data-testid={`li-qty-${i}`} />
                      </div>
                      <Input placeholder="unit" value={li.unit} onChange={(e) => updateLI(i, "unit", e.target.value)} className="mt-1 text-xs h-7" />
                    </td>
                    <td className="py-2 pr-2 w-32 text-right font-medium text-slate-900 pt-4" data-testid={`li-amt-${i}`}>
                      {fmt(Number(li.quantity || 0) * Number(li.unit_price || 0))}
                    </td>
                    <td className="py-2 text-right pt-4">
                      <button onClick={() => removeLine(i)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`li-remove-${i}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* SECTION 5: TOTALS */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {/* SECTION 6: NOTES */}
          <Section tid="section-notes" title="Notes · Scope · Terms">
            <Textarea data-testid="quote-notes" rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Project details, scope of work, terms and conditions, notes for the customer…" />
          </Section>
        </div>
        <Section tid="section-totals" title="Totals">
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium" data-testid="t-subtotal">{fmt(subtotal)}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Discount ($)</span>
              <Input type="number" step="0.01" className="w-24 h-8 text-right" value={form.discount} onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })} data-testid="quote-discount" />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Tax %</span>
              <Input type="number" step="0.01" className="w-24 h-8 text-right" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} data-testid="quote-tax" />
            </div>
            <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-medium" data-testid="t-tax">{fmt(tax)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 font-display text-lg font-extrabold text-blue-600">
              <span>Total</span><span data-testid="t-total">{fmt(total)}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500 pt-1">
              <span>Balance due</span><span data-testid="t-balance">{fmt(balance)}</span>
            </div>
          </div>
        </Section>
      </div>

      {/* SECTION 8: PHOTOS */}
      <div className="mt-4">
        <Section
          tid="section-photos"
          title={`Photos (${form.photos.length})`}
          right={
            <label className="text-xs text-blue-600 hover:underline cursor-pointer" data-testid="photo-upload-label">
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} data-testid="photo-upload-input" />
              + Upload from device
            </label>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {form.photos.map((p, i) => (
              <div key={i} className="relative group" data-testid={`photo-${i}`}>
                <img src={p} alt="" className="w-full h-28 object-cover rounded-md border border-slate-200" />
                <button
                  onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                  className="absolute top-1 right-1 bg-white/95 rounded p-1 opacity-0 group-hover:opacity-100"
                  data-testid={`photo-remove-${i}`}
                >
                  <X className="w-3 h-3 text-red-600" />
                </button>
              </div>
            ))}
            <div className="border border-dashed border-slate-300 rounded-md p-3 flex flex-col items-center justify-center min-h-[112px]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sample photos</div>
              <div className="flex gap-1">
                {stockPhotos.map((p, i) => (
                  <button key={i} data-testid={`stock-photo-${i}`} onClick={() => setForm({ ...form, photos: [...form.photos, p] })} className="p-1 border border-slate-200 rounded hover:border-blue-500">
                    <ImageIcon className="w-4 h-4 text-slate-600" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Photos are attached inside the PDF gallery your customer receives.</p>
        </Section>
      </div>

      {/* SECTION 7: SIGNATURE */}
      <div className="mt-4">
        <Section tid="section-signature" title="Customer Signature">
          <SignaturePad
            value={form.signature}
            signerName={form.signer_name}
            onChange={(v) => setForm({ ...form, signature: v })}
            onSignerName={(v) => setForm({ ...form, signer_name: v })}
          />
        </Section>
      </div>

      {/* Sticky footer save */}
      <div className="mt-8 flex flex-wrap justify-end gap-2 pb-8">
        <Button variant="outline" onClick={() => nav("/dashboard/quotes")}>Cancel</Button>
        <Button data-testid="btn-save-bottom" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => persist({})} disabled={saving}>
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Quote"}
        </Button>
      </div>

      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent className="max-w-md" data-testid="limit-dialog">
          <DialogHeader><DialogTitle>You've used all 3 free quotes</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            The Free plan includes <b>3 quotes total</b>. Upgrade to <b>Quick Quotes Pro</b> to create your next quote — unlimited quotes, full customer database, scheduling, and PDF delivery. Just $3.99/month, cancel anytime.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitOpen(false)}>Not now</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={goUpgrade} data-testid="limit-upgrade">
              Upgrade for $3.99/mo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg" data-testid="quote-send-dialog">
          <DialogHeader><DialogTitle>Send quote {number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Channel</Label>
              <Select value={sendForm.channel} onValueChange={(v) => setSendForm({ ...sendForm, channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><Mail className="w-3 h-3 inline mr-1" />Email</SelectItem>
                  <SelectItem value="sms"><MessageSquare className="w-3 h-3 inline mr-1" />Text (SMS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">To</Label>
              <Input
                value={sendForm.to}
                onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                data-testid="send-to"
                type={sendForm.channel === "email" ? "email" : "text"}
                placeholder={sendForm.channel === "email" ? "customer@example.com" : "+1 555-123-4567"}
              />
              {sendForm.channel === "email" && !sendForm.to && (
                <p className="mt-1 text-[11px] text-amber-700 font-semibold" data-testid="send-to-warning">
                  This customer has no email on file. Type one above and we'll save it to their record.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Message</Label>
              <Textarea rows={6} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} data-testid="send-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={doSend} data-testid="send-confirm"><Send className="w-4 h-4 mr-1.5" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="max-w-md" data-testid="new-customer-dialog">
          <DialogHeader><DialogTitle>Add a new customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input autoFocus value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="newcust-name" placeholder="e.g. John Smith" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="newcust-email" placeholder="customer@example.com" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
              <Input type="tel" value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="newcust-phone" placeholder="(555) 123-4567" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Address</Label>
              <Textarea rows={2} value={newCust.address} onChange={(e) => setNewCust({ ...newCust, address: e.target.value })} data-testid="newcust-address" placeholder="123 Main St, Springfield, IL 62701" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustOpen(false)}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={savingCust || !newCust.name.trim()}
              data-testid="newcust-save"
              onClick={async () => {
                if (!newCust.name.trim()) { toast.error("Name is required"); return; }
                setSavingCust(true);
                try {
                  const payload = {
                    name: newCust.name.trim(),
                    email: newCust.email.trim(),
                    phone: newCust.phone.trim(),
                    address: newCust.address.trim(),
                  };
                  const { data } = await api.post("/customers", payload);
                  setCustomers((xs) => [...xs, data].sort((a, b) => a.name.localeCompare(b.name)));
                  setForm((f) => ({ ...f, customer_id: data.id }));
                  setNewCustOpen(false);
                  toast.success(`Added ${data.name}`);
                } catch (e) {
                  toast.error(formatError(e));
                } finally {
                  setSavingCust(false);
                }
              }}
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> {savingCust ? "Adding…" : "Add customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
